import { canOpenPortalJob } from '../../../utils/portalJobNavigation';
import React from 'react';
import { 
  formatCurrency, 
  calculateLeadTime,
  formatLeadTime,
  formatDateToMMDD
} from '../utils/tableUtils';
import {
  getModelHighlightClass,
  getModelNumberHighlightClass,
  isHighPriority,
  getCompletionStatus
} from '../utils/highlightingUtils';
import { 
  hasMoveRequest, 
  hasBeenMovedUp,
  hasWitnessTest,
  getMoveRequestIcon,
  getMoveRequestTooltipText
} from '../utils/jobHelpers';
import {
  renderEngineeringColumns,
  renderFabricationColumns,
  renderControlPanelColumns,
  renderPumpsColumns,
  renderAssemblyColumns,
  renderTestColumns,
  renderNorthPrairieColumns,
  renderShippingColumns,
  renderPortalDetailsColumns
} from '../utils/columnRenderers';

const JobScheduleRow = ({
  job,
  index,
  handleRowClick,
  highlightedRow,
  handleOpenModal,
  handleCellDoubleClick,
  renderEditableCell,
  hasTbd,
  hasIncompleteTasks,
  filters,
  weekColumns,
  showActionColumn,
  pumpDetails // Make sure this is in the props
}) => {
  const isHighlighted = highlightedRow.id === job.ID && 
    highlightedRow.week === (job.ScheduleWeek || "Unscheduled");
  
  // Calculate lead time
  const leadTime = calculateLeadTime(job.ProductionRelease, job.CurrentPlanShipDate);
  const formattedLeadTime = formatLeadTime(leadTime);
  const isControlPanelView = Boolean(filters.controlPanelView);

  // Software view shows only specific columns
  if (filters.softwareView) {
    return (
      <tr
        onClick={() => handleRowClick(job)}
        style={{
          backgroundColor: isHighlighted ? "#e6faff" : "transparent",
        }}
        className={`${index % 2 === 0 ? 'job-row-even' : 'job-row-odd'}`}
      >
        {showActionColumn && (
          <td className="action-column editable-cell">
            <button
              type="button"
              className="schedule-row-edit-button"
              onClick={(e) => handleOpenModal(e, job)}
              aria-label={`Edit ${job.JobNumber || job.ProjectName || 'job'}`}
              title="Edit job"
            >
              <i className="bi bi-pencil" aria-hidden="true"></i>
              <span className="schedule-row-edit-label">Edit</span>
            </button>
          </td>
        )}
        {filters.compactInlineIdentity && (
          <>
            <td className="compact-inline-project-column">{job.ProjectName || "No project name"}</td>
            <td className="compact-inline-model-column">{job.ModelNumber || "No model number"}</td>
          </>
        )}
        <td className="row-number-column">
          {/* Conditionally wrap row number in link if ScheduleOnly is not true, "Yes", or "yes" */}
          {canOpenPortalJob(job) ? (
            <a 
              href={`/${job.ID}/generalInfo`} 
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={(e) => e.stopPropagation()} // Prevent row click when clicking link
            >
              {index + 1}
            </a>
          ) : (
            index + 1
          )}
        </td>
        <td className={`job-number-column editable-cell ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
            onDoubleClick={(e) => handleCellDoubleClick(e, job, "JobNumber")}>
          {renderEditableCell(job, "JobNumber", 
            <>
              {job.JobNumber || ""}
              {hasTbd(job.ID) && (
                <i
                  className="bi bi-exclamation-lg ms-1"
                  style={{ color: "red" }}
                  title="This job has TBDs"
                ></i>
              )}
              {hasIncompleteTasks && hasIncompleteTasks(job.ID) && (
                <i
                  className="bi bi-person-lines-fill ms-1"
                  style={{ color: "orange" }}
                  title="This job has incomplete change order tasks"
                ></i>
              )}
              {hasMoveRequest(job) && (
                <i
                  className={`${getMoveRequestIcon(job)} ms-1`}
                  style={{ color: "blue" }}
                  title={getMoveRequestTooltipText(job)}
                ></i>
              )}
              {hasBeenMovedUp(job) && !hasMoveRequest(job) && (
                <i
                  className={`${getMoveRequestIcon(job)} ms-1`}
                  style={{ color: "green" }}
                  title={getMoveRequestTooltipText(job)}
                ></i>
              )}
              {hasWitnessTest(job) && (
                <i
                  className="bi bi-search ms-1"
                  style={{ color: "black" }}
                  title="This job has a witness test"
                ></i>
              )}
              <span className="schedule-compact-job-context">
                <span className="schedule-compact-project-name">{job.ProjectName || "No project name"}</span>
                <span className="schedule-compact-model-number">{job.ModelNumber || "No model number"}</span>
              </span>
            </>
          )}
        </td>
        <td className={`sticky-column editable-cell ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
            onDoubleClick={(e) => handleCellDoubleClick(e, job, "ProjectName")}>
          {renderEditableCell(job, "ProjectName", job.ProjectName || "")}
        </td>
        <td className={`model-number-column editable-cell ${(getModelHighlightClass(job) || getModelNumberHighlightClass(job.ModelNumber))} ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
            onDoubleClick={(e) => handleCellDoubleClick(e, job, "ModelNumber")}>
          {renderEditableCell(job, "ModelNumber", job.ModelNumber || "")}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareType")} data-column-id="softwareType">
          {renderEditableCell(job, "SoftwareType", job.SoftwareType || "")}
        </td>
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareDue")} data-column-id="softwareDue">
          {renderEditableCell(job, "SoftwareDue", formatDateToMMDD(job.SoftwareDue) || "")}
        </td>
        <td className={`editable-cell ${getCompletionStatus(job.SoftwareDone, job.SoftwareDue)}`} onDoubleClick={(e) => handleCellDoubleClick(e, job, "SoftwareDone")} data-column-id="softwareDone">
          {renderEditableCell(job, "SoftwareDone", formatDateToMMDD(job.SoftwareDone) || "")}
        </td>
      </tr>
    );
  }

  // Regular view (existing code)
  return (
    <tr
      onClick={() => handleRowClick(job)}
      style={{
        backgroundColor: isHighlighted ? "#e6faff" : "transparent",
      }}
      className={`${index % 2 === 0 ? 'job-row-even' : 'job-row-odd'}`}
    >
      {showActionColumn && (
        <td className="action-column editable-cell">
          <button
            type="button"
            className="schedule-row-edit-button"
            onClick={(e) => handleOpenModal(e, job)}
            aria-label={`Edit ${job.JobNumber || job.ProjectName || 'job'}`}
            title="Edit job"
          >
            <i className="bi bi-pencil" aria-hidden="true"></i>
            <span className="schedule-row-edit-label">Edit</span>
          </button>
        </td>
      )}
      {filters.compactInlineIdentity && (
        <>
          <td className="compact-inline-project-column">{job.ProjectName || "No project name"}</td>
          <td className="compact-inline-model-column">{job.ModelNumber || "No model number"}</td>
        </>
      )}
      <td className="row-number-column">
        {/* Conditionally wrap row number in link if ScheduleOnly is not true, "Yes", or "yes" */}
        {canOpenPortalJob(job) ? (
          <a 
            href={`/${job.ID}/generalInfo`} 
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking link
          >
            {index + 1}
          </a>
        ) : (
          index + 1
        )}
      </td>
      <td className={`job-number-column editable-cell ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
          onDoubleClick={(e) => handleCellDoubleClick(e, job, "JobNumber")}>
        {renderEditableCell(job, "JobNumber", 
          <>
            {job.JobNumber || ""}
            {hasTbd(job.ID) && (
              <i
                className="bi bi-exclamation-lg ms-1"
                style={{ color: "red" }}
                title="This job has TBDs"
              ></i>
            )}
            {hasIncompleteTasks && hasIncompleteTasks(job.ID) && (
              <i
                className="bi bi-person-lines-fill ms-1"
                style={{ color: "orange" }}
                title="This job has incomplete change order tasks"
              ></i>
            )}
            {hasMoveRequest(job) && (
              <i
                className={`${getMoveRequestIcon(job)} ms-1`}
                style={{ color: "blue" }}
                title={getMoveRequestTooltipText(job)}
              ></i>
            )}
            {hasBeenMovedUp(job) && !hasMoveRequest(job) && (
              <i
                className={`${getMoveRequestIcon(job)} ms-1`}
                style={{ color: "green" }}
                title={getMoveRequestTooltipText(job)}
              ></i>
            )}
            {hasWitnessTest(job) && (
              <i
                className="bi bi-search ms-1"
                style={{ color: "black" }}
                title="This job has a witness test"
              ></i>
            )}
            <span className="schedule-compact-job-context">
              <span className="schedule-compact-project-name">{job.ProjectName || "No project name"}</span>
              <span className="schedule-compact-model-number">{job.ModelNumber || "No model number"}</span>
            </span>
          </>
        )}
      </td>
      <td className={`model-number-column editable-cell ${(getModelHighlightClass(job) || getModelNumberHighlightClass(job.ModelNumber))} ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
          onDoubleClick={(e) => handleCellDoubleClick(e, job, "ModelNumber")}>
        {renderEditableCell(job, "ModelNumber", job.ModelNumber || "")}
      </td>
      <td className={`sticky-column editable-cell ${isHighPriority(job) ? 'highlight-high-priority' : ''}`} 
          onDoubleClick={(e) => handleCellDoubleClick(e, job, "ProjectName")}>
        {renderEditableCell(job, "ProjectName", job.ProjectName || "")}
      </td>
      {!filters.dueDatesOnly && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "OrderDate")}>
          {renderEditableCell(job, "OrderDate", formatDateToMMDD(job.OrderDate) || "")}
        </td>
      )}
      {!filters.dueDatesOnly && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ProductionRelease")}>
          {renderEditableCell(job, "ProductionRelease", formatDateToMMDD(job.ProductionRelease) || "")}
        </td>
      )}
      {!filters.dueDatesOnly && (
        <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "ReleaseDate")}>
          {renderEditableCell(job, "ReleaseDate", formatDateToMMDD(job.ReleaseDate) || "")}
        </td>
      )}
      
      {/* Department columns using new renderers */}
      {renderEngineeringColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {renderFabricationColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {renderControlPanelColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {/* Pass pumpDetails here */}
      {!isControlPanelView && renderPumpsColumns(job, filters, handleCellDoubleClick, renderEditableCell, pumpDetails)}
      {renderAssemblyColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {renderTestColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {!isControlPanelView && renderNorthPrairieColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      {renderShippingColumns(job, filters, handleCellDoubleClick, renderEditableCell, weekColumns)}
      {renderPortalDetailsColumns(job, filters, handleCellDoubleClick, renderEditableCell)}
      
      {/* Notes, Payment Terms, and Dollar Value */}
      <td className="notes-column editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "Notes")}>
        {renderEditableCell(job, "Notes", job.Notes || "")}
      </td>
      {!isControlPanelView && (
        <>
          <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "PaymentTerms")}>
            {renderEditableCell(job, "PaymentTerms", job.PaymentTerms || "")}
          </td>
          {!filters.dueDatesOnly && (
            <td className="editable-cell" onDoubleClick={(e) => handleCellDoubleClick(e, job, "DollarValue")}>
              {renderEditableCell(job, "DollarValue", formatCurrency(job.DollarValue))}
            </td>
          )}
        </>
      )}
      
      
      {/* Lead Time column */}
      {!isControlPanelView && (
        <td className={leadTime?.isNegative ? 'text-danger' : ''}>
          {formattedLeadTime}
        </td>
      )}
    </tr>
  );
};

export default JobScheduleRow;
